import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { getDashboardSummary, getSystemHealth, remoteAction } from '../api/admin.js';
import type {
  AdminRemoteAction,
  DashboardAlert,
} from '@m8-app/contracts/rest/admin.js';

const ACTIONS: AdminRemoteAction[] = ['reboot', 'reset_wifi', 'activate', 'firmware_update'];

export function Dashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ['dashboardSummary'],
    queryFn: () => getDashboardSummary(),
    refetchInterval: 30_000,
  });
  const { data: health } = useQuery({
    queryKey: ['systemHealth'],
    queryFn: () => getSystemHealth(),
    refetchInterval: 60_000,
  });

  const d = data?.devices;
  const u = data?.users;
  const s = data?.serviceRequests;

  return (
    <div className="p-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Dashboard</h2>

      <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Devices</h3>
      <div className="grid grid-cols-4 gap-4 mb-6">
        <StatCard label="Total"      value={isLoading ? '…' : String(d?.total ?? 0)}              color="indigo" />
        <StatCard label="Active"     value={isLoading ? '…' : String(d?.active ?? 0)}             color="green"  />
        <StatCard label="Registered" value={isLoading ? '…' : String(d?.registered ?? 0)}         color="green"  />
        <StatCard label="Pending"    value={isLoading ? '…' : String(d?.registrationPending ?? 0)} color="amber"  />
        <StatCard label="Deactivated" value={isLoading ? '…' : String(d?.deactivated ?? 0)}       color="red"    />
        <StatCard label="Online"     value={isLoading ? '…' : String(d?.online ?? 0)}             color="green"  />
        <StatCard label="Offline"    value={isLoading ? '…' : String(d?.offline ?? 0)}            color="red"    />
      </div>

      <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Users & Service</h3>
      <div className="grid grid-cols-4 gap-4 mb-8">
        <StatCard label="Total Users"     value={isLoading ? '…' : String(u?.total ?? 0)}    color="indigo" />
        <StatCard label="Blocked Users"   value={isLoading ? '…' : String(u?.blocked ?? 0)}  color="red"    />
        <StatCard label="Open Services"   value={isLoading ? '…' : String(s?.open ?? 0)}     color="amber"  />
        <StatCard label="In Review"       value={isLoading ? '…' : String(s?.inReview ?? 0)} color="blue"   />
      </div>

      {/* Alerts */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <h3 className="text-base font-semibold text-gray-900 mb-3">Alerts</h3>
        {data?.alerts.length === 0 && (
          <p className="text-sm text-gray-400">No alerts. All clear.</p>
        )}
        <ul className="divide-y divide-gray-100">
          {data?.alerts.map((a) => <AlertRow key={a.id} a={a} />)}
        </ul>
      </div>

      {/* System Health */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <h3 className="text-base font-semibold text-gray-900">System Health</h3>
          {health && (
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
              health.status === 'ok' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
            }`}>
              {health.status.toUpperCase()}
            </span>
          )}
        </div>
        {!health ? (
          <p className="text-sm text-gray-400">Loading health data…</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${health.db.ok ? 'bg-green-500' : 'bg-red-500'}`} />
              <span className="text-gray-600">Database</span>
              <span className="ml-auto text-gray-400">{health.db.latencyMs}ms</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${health.redis.ok ? 'bg-green-500' : 'bg-red-500'}`} />
              <span className="text-gray-600">Redis</span>
              <span className="ml-auto text-gray-400">{health.redis.latencyMs}ms</span>
            </div>
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${health.mqtt.connected ? 'bg-green-500' : 'bg-red-500'}`} />
              <span className="text-gray-600">MQTT</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-600">Commands (24h)</span>
              <span className="ml-auto text-gray-700 font-medium">{health.commandsLast24h}</span>
            </div>
          </div>
        )}
      </div>

      <RemoteActionsCard />
    </div>
  );
}

function AlertRow({ a }: { a: DashboardAlert }) {
  const colors = {
    info:     'bg-blue-50  text-blue-700',
    warning:  'bg-amber-50 text-amber-700',
    critical: 'bg-red-50   text-red-700',
  } as const;
  const targetLink = a.serviceRequestId
    ? `/services/${a.serviceRequestId}`
    : a.deviceId
      ? `/devices/${a.deviceId}`
      : null;

  return (
    <li className="flex items-center justify-between py-2.5">
      <div className="flex items-center gap-3">
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium uppercase ${colors[a.severity]}`}>
          {a.severity}
        </span>
        <span className="text-sm text-gray-700">{a.message}</span>
      </div>
      {targetLink && (
        <Link to={targetLink} className="text-sm text-indigo-600 hover:underline">View</Link>
      )}
    </li>
  );
}

function RemoteActionsCard() {
  const [deviceId, setDeviceId] = useState('');
  const [action, setAction] = useState<AdminRemoteAction>('reboot');
  const [cmdResult, setCmdResult] = useState<{ cmdId: string } | null>(null);

  const { mutate: sendAction, isPending: sending, error: actionError } = useMutation({
    mutationFn: () => remoteAction(deviceId, { action }),
    onSuccess: (res) => setCmdResult({ cmdId: res.cmdId }),
  });

  function handleAction(e: FormEvent) {
    e.preventDefault();
    setCmdResult(null);
    sendAction();
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 max-w-lg">
      <h3 className="text-base font-semibold text-gray-900 mb-4">Remote Device Action</h3>
      <form onSubmit={handleAction} className="space-y-3">
        <div>
          <label htmlFor="deviceId" className="block text-sm font-medium text-gray-700 mb-1">Device ID</label>
          <input
            id="deviceId"
            type="text"
            placeholder="UUID"
            value={deviceId}
            onChange={(e) => setDeviceId(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            required
          />
        </div>
        <div>
          <label htmlFor="remoteAction" className="block text-sm font-medium text-gray-700 mb-1">Action</label>
          <select
            id="remoteAction"
            value={action}
            onChange={(e) => setAction(e.target.value as AdminRemoteAction)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {ACTIONS.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        {actionError && (
          <p className="text-sm text-red-600">
            {(actionError as { message?: string }).message ?? 'Action failed'}
          </p>
        )}
        {cmdResult && (
          <p className="text-sm text-green-700 bg-green-50 rounded px-3 py-2">
            Sent — cmdId: <code className="font-mono">{cmdResult.cmdId}</code>
          </p>
        )}
        <button
          type="submit"
          disabled={sending}
          className="w-full bg-indigo-600 text-white py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors"
        >
          {sending ? 'Sending…' : 'Send Action'}
        </button>
      </form>
    </div>
  );
}

function StatCard({
  label, value, color,
}: { label: string; value: string; color: 'green' | 'red' | 'blue' | 'amber' | 'indigo' }) {
  const colors = {
    green:  'bg-green-50  border-green-200  text-green-700',
    red:    'bg-red-50    border-red-200    text-red-700',
    blue:   'bg-blue-50   border-blue-200   text-blue-700',
    amber:  'bg-amber-50  border-amber-200  text-amber-700',
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-700',
  };
  return (
    <div className={`rounded-xl border p-4 ${colors[color]}`}>
      <p className="text-xs font-medium opacity-80">{label}</p>
      <p className="text-2xl font-bold mt-1">{value}</p>
    </div>
  );
}
